// File: frontend/src/pages/ClientPortalPage.js
// Version: 2.0 (quick-260915-d2d — зашитые цвета убраны, экран на общем слое)
// Date: 2026-09-15
// Description: Client portal dashboard — case list, timeline, documents, and messages.

import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-hot-toast';
import clientPortalService from '../services/clientPortalService';
import { useUiLanguage } from '../context/UiLanguageContext';
// 🔴 ОБЩИЙ СЛОЙ ОФОРМЛЕНИЯ. `Sostoyanie` — единственный держатель того, КАК
//    экран говорит «грузим», «пусто» и «не смогли спросить». До этой правки
//    каждая из четырёх таких фраз несла собственный набор классов, и разойтись
//    им было нечем — разошлись бы МОЛЧА.
import { Sostoyanie } from '../components/FitenkoUzor';
import './ClientPortalPage.css';

// --- Helpers ---
// 🔴 Цвет таблетки состояния держится в ОДНОМ месте — `ClientPortalPage.css`.
//    Здесь возвращается ИМЯ РОЛИ, а не цвет: у «в работе» своего триплета shadcn
//    нет (янтарь живёт только в §3.1), и попытка выразить его арбитражным
//    классом вернула бы зашитый литерал в разметку.
const statusColor = (status) => {
  switch ((status || '').toLowerCase()) {
    case 'open':        return 'cp-status cp-status-open';
    case 'in_progress': return 'cp-status cp-status-progress';
    case 'closed':      return 'cp-status cp-status-closed';
    default:            return 'cp-status cp-status-closed';
  }
};

const statusLabel = (status, t) => {
  switch ((status || '').toLowerCase()) {
    case 'open':        return t('practice.portal.status_open');
    case 'in_progress': return t('practice.portal.status_in_progress');
    case 'closed':      return t('practice.portal.status_closed');
    default:            return (status || '').replace('_', ' ');
  }
};

const formatDate = (iso, lang) => {
  if (!iso) return '—';
  const locale = lang === 'ru' ? 'ru-RU' : 'en-US';
  return new Date(iso).toLocaleDateString(locale, { year: 'numeric', month: 'short', day: 'numeric' });
};

// --- Sub-components ---

const CaseCard = ({ caseItem, isSelected, onClick, t, lang }) => (
  <div
    className={`case-card cursor-pointer ${isSelected ? 'active' : ''}`}
    onClick={() => onClick(caseItem)}
  >
    <div className="flex items-start justify-between gap-2">
      <div>
        <p className="text-xs cp-ink-accent font-bold tracking-wider font-mono uppercase" data-user-content="true">{caseItem.case_number}</p>
        <p className="font-bold text-foreground text-base mt-1 cp-display" data-user-content="true">
          {caseItem.title || caseItem.case_type}
        </p>
      </div>
      <span className={`px-2.5 py-1 rounded-md text-xs font-bold whitespace-nowrap uppercase tracking-wider ${statusColor(caseItem.status)}`}>
        {statusLabel(caseItem.status, t)}
      </span>
    </div>
    <p className="text-xs text-muted-foreground mt-3">{t('practice.portal.opened_date', { date: formatDate(caseItem.created_at, lang) })}</p>
  </div>
);

const TimelineTab = ({ timeline, t, lang }) => {
  if (!timeline || timeline.length === 0) {
    return <div className="py-6 text-center italic"><Sostoyanie tekst={t('practice.portal.no_timeline')} /></div>;
  }
  const sorted = [...timeline].sort((a, b) => new Date(a.event_date) - new Date(b.event_date));
  return (
    <ul className="space-y-4 py-2">
      {sorted.map((event) => (
        <li key={event.id} className="flex gap-4 items-start">
          <div className="timeline-item-marker flex-shrink-0" />
          <div className="bg-accent-soft border border-border rounded-lg p-3 flex-1">
            <p className="text-sm font-semibold text-foreground" data-user-content="true">{event.description}</p>
            <p className="text-xs text-muted-foreground mt-1">{formatDate(event.event_date, lang)} — <span className="capitalize font-medium cp-ink-accent" data-user-content="true">{event.event_type}</span></p>
          </div>
        </li>
      ))}
    </ul>
  );
};

const DocumentsTab = ({ caseId, documents, t }) => {
  if (!documents || documents.length === 0) {
    return <div className="py-6 text-center italic"><Sostoyanie tekst={t('practice.portal.no_documents')} /></div>;
  }
  return (
    <ul className="space-y-2.5 py-2">
      {documents.map((doc) => (
        <li key={doc.id} className="flex items-center justify-between bg-card border border-border rounded-lg px-4 py-3 hover:border-primary/40 transition-colors">
          <span className="text-sm font-semibold text-foreground truncate max-w-sm" data-user-content="true">{doc.filename}</span>
          <a
            href={`/api/v1/documents/${doc.id}/download`}
            target="_blank"
            rel="noopener noreferrer"
            className="ml-3 text-xs font-bold uppercase tracking-wider cp-ink-accent hover:underline whitespace-nowrap bg-accent-soft px-3 py-1.5 rounded border border-primary/30"
          >
            {t('practice.portal.download')}
          </a>
        </li>
      ))}
    </ul>
  );
};

const MessagesTab = ({ caseId, messages, onMessageSent, t, lang }) => {
  const [newMessage, setNewMessage] = useState('');
  const [sending, setSending] = useState(false);

  const handleSend = async () => {
    const trimmed = newMessage.trim();
    if (!trimmed) return;
    setSending(true);
    try {
      const sent = await clientPortalService.sendMessage(caseId, trimmed);
      setNewMessage('');
      onMessageSent(sent);
    } catch (err) {
      console.error('[ClientPortal] sendMessage error:', err);
      toast.error(t('practice.portal.failed_send_message'));
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="flex flex-col gap-4 py-2">
      <div className="messages-thread space-y-3 max-h-80 overflow-y-auto pr-1">
        {(!messages || messages.length === 0) && (
          <div className="py-6 text-center italic"><Sostoyanie tekst={t('practice.portal.no_messages')} /></div>
        )}
        {(messages || []).map((msg) => (
          <div
            key={msg.id}
            className={`portal-msg-bubble ${msg.is_attorney_reply ? 'attorney' : 'client'}`}
          >
            <p data-user-content="true">{msg.body}</p>
            <p className="text-[11px] opacity-60 mt-1 text-right">{formatDate(msg.created_at, lang)}</p>
          </div>
        ))}
      </div>
      <div className="flex gap-2.5 mt-2">
        <textarea
          className="flex-1 border border-border rounded-lg px-3 py-2 text-sm bg-card text-foreground resize-none focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/20"
          rows={2}
          placeholder={t('practice.portal.type_message_placeholder')}
          value={newMessage}
          onChange={(e) => setNewMessage(e.target.value)}
          data-user-content="true"
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              handleSend();
            }
          }}
        />
        <button
          className="px-5 py-2 bg-foreground text-white text-sm font-semibold rounded-lg hover:opacity-80 disabled:opacity-50 transition-colors self-end h-[42px]"
          onClick={handleSend}
          disabled={sending || !newMessage.trim()}
        >
          {sending ? t('practice.portal.sending') : t('practice.portal.send_btn')}
        </button>
      </div>
    </div>
  );
};

const CaseDetail = ({ caseItem, onMessageSent, t, lang }) => {
  const [activeTab, setActiveTab] = useState('timeline');
  const [documents, setDocuments] = useState(caseItem.documents || []);
  const [messages, setMessages] = useState([]);
  const [loadingMessages, setLoadingMessages] = useState(false);

  useEffect(() => {
    setDocuments(caseItem.documents || []);
    setActiveTab('timeline');
    setMessages([]);
  }, [caseItem.id]);

  useEffect(() => {
    if (activeTab === 'messages') {
      setLoadingMessages(true);
      clientPortalService.getMessages(caseItem.id)
        .then((data) => setMessages(data || []))
        .catch((err) => {
          console.error('[ClientPortal] getMessages error:', err);
          toast.error(t('practice.portal.failed_load_messages'));
        })
        .finally(() => setLoadingMessages(false));
    }
  }, [activeTab, caseItem.id, t]);

  const handleMessageSent = (newMsg) => {
    setMessages((prev) => [...prev, newMsg]);
    if (onMessageSent) onMessageSent(newMsg);
  };

  const TABS = [
    { key: 'timeline',  label: t('practice.portal.tab_timeline') },
    { key: 'documents', label: t('practice.portal.tab_documents') },
    { key: 'messages',  label: t('practice.portal.tab_messages') },
  ];

  return (
    <div className="case-detail-card">
      <div className="mb-6 pb-4 border-b border-border">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div>
            <span className="text-xs font-mono font-bold tracking-wider cp-ink-accent uppercase" data-user-content="true">{caseItem.case_number}</span>
            <h2 className="text-xl font-bold text-foreground mt-1 cp-display" data-user-content="true">
              {caseItem.title || caseItem.case_type}
            </h2>
          </div>
          <span className={`px-3 py-1 rounded-md text-xs font-bold uppercase tracking-wider ${statusColor(caseItem.status)}`}>
            {statusLabel(caseItem.status, t)}
          </span>
        </div>
        <p className="text-xs text-muted-foreground mt-1.5">{t('practice.portal.opened_date', { date: formatDate(caseItem.created_at, lang) })}</p>
      </div>

      {/* Tabs */}
      <div className="case-tabs-nav">
        {TABS.map((tab) => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            className={`case-tab-btn ${activeTab === tab.key ? 'active' : ''}`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <div className="pt-2">
        {activeTab === 'timeline' && <TimelineTab timeline={caseItem.timeline} t={t} lang={lang} />}
        {activeTab === 'documents' && <DocumentsTab caseId={caseItem.id} documents={documents} t={t} />}
        {activeTab === 'messages' && (
          loadingMessages
            ? <div className="py-6 text-center italic"><Sostoyanie tekst={t('practice.portal.loading_messages')} /></div>
            : <MessagesTab caseId={caseItem.id} messages={messages} onMessageSent={handleMessageSent} t={t} lang={lang} />
        )}
      </div>
    </div>
  );
};

// --- Main Page ---
const ClientPortalPage = () => {
  const { t, lang } = useUiLanguage();
  const [cases, setCases] = useState([]);
  const [selectedCase, setSelectedCase] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchCases = useCallback(async () => {
    setLoading(true);
    try {
      const data = await clientPortalService.getCases();
      setCases(data || []);
    } catch (err) {
      console.error('[ClientPortal] getCases error:', err);
      toast.error(t('practice.portal.failed_load_cases'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    fetchCases();
  }, [fetchCases]);

  const handleSelectCase = (caseItem) => {
    setSelectedCase(caseItem);
  };

  return (
    <div className="client-portal-container">
      <div className="client-portal-header">
        <h1 className="page-title">{t('practice.portal.title')}</h1>
        <p className="page-subtitle">{t('practice.portal.subtitle')}</p>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-24">
          <div className="animate-spin rounded-full h-10 w-10 border-2 border-border border-t-primary" />
          <span className="ml-3.5"><Sostoyanie tekst={t('practice.portal.loading')} /></span>
        </div>
      ) : (
        <div className="client-portal-layout">
          {/* Case List Column */}
          <div className="case-list-column">
            <h2 className="column-heading">{t('practice.portal.cases_heading')} ({cases.length})</h2>
            {cases.length === 0 ? (
              <div className="bg-white border border-border rounded-xl p-6 text-center">
                <div className="italic"><Sostoyanie tekst={t('practice.portal.no_cases')} /></div>
              </div>
            ) : (
              <div className="space-y-3">
                {cases.map((c) => (
                  <CaseCard
                    key={c.id}
                    caseItem={c}
                    isSelected={selectedCase?.id === c.id}
                    onClick={handleSelectCase}
                    t={t}
                    lang={lang}
                  />
                ))}
              </div>
            )}
          </div>

          {/* Case Detail Column */}
          <div className="case-detail-column">
            {selectedCase ? (
              <CaseDetail caseItem={selectedCase} onMessageSent={() => {}} t={t} lang={lang} />
            ) : (
              <div className="flex flex-col items-center justify-center h-full text-center py-20 bg-white border border-border rounded-xl text-muted-foreground">
                <div className="w-14 h-14 mb-3 rounded-full bg-accent-soft flex items-center justify-center border border-primary/30">
                  <svg className="w-7 h-7 cp-ink-accent" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
                      d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                </div>
                <p className="text-base font-medium text-foreground cp-display">
                  {t('practice.portal.select_case_prompt')}
                </p>
                <p className="text-xs text-muted-foreground mt-1 max-w-xs">
                  {t('practice.portal.select_case_sub')}
                </p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ClientPortalPage;
